import React, { forwardRef } from 'react'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme) => ({
  truncatedText: {
    cursor: 'pointer',
    width: '150px',
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  noObservation: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    width: '150px',
    display: 'inline-block',
  },
}))

/**
 * @typedef {object} TruncatedObservationProps
 * @property {string} [text] - El texto a mostrar truncado. Si es falsy o '--', muestra un placeholder.
 * @property {function(): void} [onClick] - Callback ejecutado cuando se hace clic en el texto truncado.
 */

/**
 * Un componente que muestra texto truncado con ellipsis y lo hace clickeable.
 *
 * Renderiza un texto en un contenedor de ancho fijo con overflow: ellipsis, ideal para
 * mostrar observaciones o comentarios largos en espacios limitados como celdas de tabla.
 * Al hacer hover, el texto se subraya y muestra un tooltip con el texto completo.
 * Si no hay texto, muestra un placeholder '--' en estilo secundario.
 *
 * @component
 * @example
 * const MyTable = () => {
 *   const [dialogOpen, setDialogOpen] = useState(false);
 *   const [selectedText, setSelectedText] = useState('');
 *
 *   const handleClick = (observation) => {
 *     setSelectedText(observation);
 *     setDialogOpen(true);
 *   };
 *
 *   return (
 *     <TableCell>
 *       <TruncatedObservation
 *         text="Esta es una observación muy larga que será truncada con ellipsis"
 *         onClick={() => handleClick("Esta es una observación muy larga que será truncada con ellipsis")}
 *       />
 *     </TableCell>
 *   );
 * };
 *
 * @param {TruncatedObservationProps} props - Las props del componente TruncatedObservation.
 * @param {React.Ref} ref - Referencia forwarded al elemento span.
 */
export const TruncatedObservation = forwardRef(({ text, onClick }, ref) => {
  const classes = useStyles()

  if (!text || text === '--') {
    return (
      <span ref={ref} className={classes.noObservation}>
        --
      </span>
    )
  }

  const handleClick = () => {
    if (onClick && typeof onClick === 'function') {
      onClick()
    }
  }

  return (
    <span
      ref={ref}
      className={classes.truncatedText}
      onClick={handleClick}
      title={`${text} - Clic para ver completo`}
    >
      {text}
    </span>
  )
})
